import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";

/**
 * Mini-casino bottom-sheet lobby (ludoexchange).
 * Opened from the footer "Casino" button. Renders the mini-casino auth
 * iframe docked just above the bottom navigation, with a close (X) button —
 * mirroring the reference exchange's casino lobby sheet.
 *
 * NOTE: the launch URL is hardcoded for now (short-lived provider token).
 * This should be replaced with a backend-issued URL once the ludoexchange
 * launch endpoint is available.
 */

const MINI_CASINO_URL =
  "https://minicasino.ludoexchange.com/auth/eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJlbmNyeXB0ZWRUb2tlbiI6IlUyRnNkR1ZrWDE5M3lSQ0gvMEt2NW5jRVd1ZXB1aVVHb01pWkc3bzR5NXJHTTdQQkNSYUVtSkJ3VURNdUJSdVpqSk40MnRaV28xMnQzMFlIVk1meHQzckRVRTl4WEhVVDFTQmpRRnhFVUo5YlVBZ092Ukl1dTA0bzRHbTJKZFNwc0U0TzBqL0pkT29nbjF1Wnhva0dEUVZTeGtwaTM2UWxhWTJiNmREYVdFaFFsVTZrek1iSVUza2sxNlRIcjlZVzZFbW1GclBTbEVKRjZUbmpwNUxYaE1kY0lheTlRMm5mSWxacHp5Z3EwdGI5dVF4dUZ1T1F2ZTJUdUFQS29xUm9jbnRHd3NrbWYwY1YyRWpHdjdkcXFOVGNaTXFsQTRuRlZ2VnU3YkJzM0QzMllyS2E1VUluWWZqQ3V2Q2M0NUJURkNpc2N0WHZhMHhjSUNCLzdjZGdLUT09IiwiaWF0IjoxNzkyNzQzMjk4LCJleHAiOjE3OTI3NjEyOTh9.971AvKRPPXkBzK8frl_Uin1QKzRSMrzEcchgjozjaXc";

const MiniCasinoSheet = ({ open, onClose }) => {
  const dialogRef = useRef(null);

  useEffect(() => {
    if (!open) return;

    const dialog = dialogRef.current;
    const previousBodyOverflow = document.body.style.overflow;
    const previousRootOverflow = document.documentElement.style.overflow;

    // A modal dialog makes the page and footer inert, including keyboard input.
    dialog.showModal();
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";

    return () => {
      if (dialog.open) dialog.close();
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousRootOverflow;
    };
  }, [open]);

  if (!open) return null;

  return createPortal(
    <>
      <style>{`
        .mini-casino-sheet::backdrop {
          background: rgba(0, 0, 0, 0.35);
          touch-action: none;
        }
        .mini-casino-sheet-close:focus-visible {
          outline: 2px solid #fff;
          outline-offset: 2px;
        }
      `}</style>
      <dialog
        ref={dialogRef}
        className="mini-casino-sheet"
        aria-label="Casino lobby"
        aria-modal="true"
        onCancel={(event) => {
          event.preventDefault();
          onClose();
        }}
        style={{
          position: "fixed",
          top: "auto",
          left: 0,
          right: 0,
          bottom: "12.5vw", // sit just above the bottom navigation bar
          width: "100%",
          maxWidth: "480px",
          maxHeight: "calc(100dvh - 12.5vw - 12px)",
          margin: "0 auto",
          padding: 0,
          border: "none",
          background: "#000",
          color: "#fff",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          overscrollBehavior: "contain",
          boxShadow: "0 -6px 18px rgba(0,0,0,0.35)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexShrink: 0,
            padding: "4px 12px",
            background: "#000",
            color: "#fff",
            fontSize: "14px",
            fontWeight: "bold",
          }}
        >
          <span>Casino</span>
          <button
            type="button"
            className="mini-casino-sheet-close"
            aria-label="Close casino"
            autoFocus
            onClick={onClose}
            style={{
              background: "transparent",
              border: "none",
              padding: 0,
              font: "inherit",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              minHeight: "36px",
              cursor: "pointer",
              color: "#fff",
            }}
          >
            Close
            <span aria-hidden="true" style={{ color: "#ff2400", fontSize: "28px", lineHeight: 1 }}>×</span>
          </button>
        </div>
        <iframe
          id="miniCasinoIframe"
          title="Mini Casino"
          src={MINI_CASINO_URL}
          height="220"
          width="100%"
          style={{
            border: "none",
            display: "block",
            flex: "1 1 220px",
            minHeight: 0,
            pointerEvents: "auto",
            overflow: "hidden",
          }}
        />
      </dialog>
    </>,
    document.body,
  );
};

export default MiniCasinoSheet;
